<script>
	const Q = require('q');
	const assignmentService = require('services/assignmentService');
	const apiDefinitions = require('../../../../../../../lib/services/api/core/apiDefinitions');
	const { assetUrl } = require('utilities/urlUtilities');
	const pubsub = require('events/pubsub');
	const { getWordCount } = require('utilities');
	const { studentSaveOfflineBoK } = require('utilities/offlineUtilities');

	const analyticsUtilities = require('utilities/analyticsUtilities');
	const ELL_AUDIO_PLAY = analyticsUtilities.events.ELL_AUDIO_PLAY;
	const ELL_AUDIO_PAUSE = analyticsUtilities.events.ELL_AUDIO_PAUSE;
	const ELL_AUDIO_COMPLETE = analyticsUtilities.events.ELL_AUDIO_COMPLETE;
	const QUESTIONS_VIEW = 'questions';
	const BOOK_OF_KNOWLEDGE_VIEW = 'book-of-knowledge';

	module.exports = {
		props: {
			workspace: {
				default: () => ({})
			},
			'route-context': {},

			// if is-demo is true then demo-text should be displayed and submitting should be disabled
			'is-demo': {
				required: false,
				default: false,
				type: Boolean
			},
			'demo-text': {
				required: false,
				default: '',
				type: String
			},
			'is-ell': {
				required: false,
				default: false,
				type: Boolean
			},
			'ell-bok-directions-label': {
				required: false,
				default: '',
				type: String
			},
			'ell-bok-instruction-text': {
				required: false,
				default: '',
				type: String
			},
			'ell-bok-instruction-audio-url': {
				required: false,
				default: '',
				type: String
			},
			'ell-rtl': {
				required: false,
				default: false,
				type: Boolean
			},
			'ell-lang': {
				required: false,
				default: undefined
			},
			assignmentPastDeadline: {
				required: false
			}
		},

		components: {
			'audio-player-simple': require('components/audio-player-simple.vue').default,
			'validated-textarea': require('components/validated-textarea.vue').default
		},

		modelData: ['activeProduct', 'offlineMode', 'disableCopyPaste', 'wsTab', 'activeClass'],

		computed: {
			parentPid() {
				let activeProduct = this.activeProduct;
				return activeProduct.uuid;
			},
			ellBokInstructionAudioAssetUrl() {
				return assetUrl(this.ellBokInstructionAudioUrl);
			},
			workspaceSubmitted() {
				return this.workspace?.scoreState?.a[this.routeContext.params.pid]?.s === 's';
			},
			submitDisabled() {
				const bok = this.form.bok || '';
				const characterCount = bok.length;
				const wordCount = getWordCount(bok);

				return this.isDemo || characterCount > this.maxLength || wordCount < this.minWords || this.workspaceSubmitted;
			},
			minWords() {
				const unrestrictedGrades = new Set(['191923', '16', '17', '18']);
				const activeClass = this.activeClass;
				const grade = activeClass?.g ?? activeClass?.grade;
				return unrestrictedGrades.has(grade) ? 0 : 20;
			}
		},

		data() {
			return {
				form: {
					bok: ''
				},

				ellText: '1. I learned that \n\n\n2. I also learned that \n\n\n',

				maxLength: apiDefinitions.validation.max,

				ELL_AUDIO_PLAY,
				ELL_AUDIO_PAUSE,
				ELL_AUDIO_COMPLETE,
				QUESTIONS_VIEW,
				BOOK_OF_KNOWLEDGE_VIEW
			};
		},

		methods: {
			submit() {
				if (this.submitDisabled) {
					return Q();
				}

				if (this.workspaceSubmitted) {
					// The way finishStudentArticle is handled should make it impossible to
					// reach this (modals closed, go immediately to POST_SUBMISSION_VIEW), this
					// is just a redundant safety. The questions-tab behaves similarly.
					// There, the submit button is hidden if workspace.s === 's'
					return Q();
				}

				return (this.bokPromise || Q()).then(() => {
					pubsub.publish('submit-assignment', this.form.bok);

					var deferred = Q.defer();

					this.$subscribeOnce('bok-submitted', deferred.resolve);

					this.$subscribeOnce('modal-closed', function (modalId) {
						if (modalId === 'modal-submit-assignment') {
							deferred.resolve();
						}
					});

					return deferred.promise;
				});
			},

			saveDraft(event) {
				if (this.workspaceSubmitted) return;

				const bok = event?.target?.value ?? '';
				const ellIsBokEmpty = bok === this.ellText && this.isEll;
				const offlineMode = this.offlineMode;
				const form = this.$refs.bokForm;
				const pid = this.routeContext.params.pid;
				const aid = this.routeContext.params.aid;

				const params = {
					aid,
					pid,
					bok
				};

				if (offlineMode === false && !this.isDemo && !ellIsBokEmpty && bok && bok.trim() && form && form.checkValidity()) {
					this.bokPromise = assignmentService.saveBookOfKnowledge(params);
				} else if (offlineMode && !this.isDemo && !ellIsBokEmpty && bok && bok.trim() && form && form.checkValidity()) {
					this.bokPromise = studentSaveOfflineBoK(params);
				}
				this.bokPromise?.finally(() => {
					this.bokPromise = null;
				});
			},
			onPaste(event) {
				if (this.disableCopyPaste) {
					event.preventDefault();
				}
			}
		},

		watch: {
			isDemo: {
				handler(v) {
					if (this.isEll) {
						return;
					}
					if (v) {
						this.form.bok = this.demoText || '';
					}
				},
				immediate: true
			},

			demoText: {
				handler(v) {
					if (this.isDemo) {
						this.form.bok = v || '';
					}
				},
				immediate: true
			},

			workspace: {
				handler(value) {
					var bokSet = false;

					if (this.isDemo) {
						return;
					}

					const hasData = Object.keys(value).length > 0;

					if (hasData) {
						var scoreState = value.scoreState.a,
							parameters = this.routeContext.params;

						if (parameters) {
							var productId = parameters.pid;

							if (scoreState[productId] && scoreState[productId].b) {
								this.form.bok = scoreState[productId].b;
								bokSet = true;
							}
						}
					}

					if (!bokSet && !this.isEll) {
						this.form.bok = '';
					}
				},

				immediate: true
			},

			isEll: {
				handler(value) {
					if (value && !this.form.bok) {
						this.form.bok = this.ellText;
					}
				},

				immediate: true
			}
		},
		beforeDestroy() {
			let workspace = this.workspace;

			if (Object.keys(workspace).length > 0) {
				var scoreState = workspace.scoreState.a,
					parameters = this.routeContext.params;

				if (parameters) {
					var productId = parameters.pid;

					if (scoreState[productId]) {
						scoreState[productId].b = this.form.bok;
					}
				}
			}
		}
	};
</script>
<template>
	<div class="main-content wrapper bok-tab">
		<div
			class="auto-save font-small text-muted"
			v-if="workspace.s !== 's' && !assignmentPastDeadline && (wsTab === QUESTIONS_VIEW || wsTab === BOOK_OF_KNOWLEDGE_VIEW)">
			Your work is automatically saved.
		</div>
		<p v-if="!isEll"><strong>Directions:&nbsp;</strong>Please write 2 or 3 things you have learned from the article that you want to remember.</p>
		<p
			v-else
			:class="{
				'ell-rtl': ellRtl
			}">
			<strong>{{ ellBokDirectionsLabel }}</strong> {{ ellBokInstructionText }}
			<audio-player-simple
				v-if="offlineMode === false"
				:url="ellBokInstructionAudioAssetUrl"
				:event-data="{
					parentPid: parentPid,
					pid: routeContext.params.pid,
					aid: routeContext.params.aid,
					lang: ellLang !== 'en' ? ellLang : undefined,
					tag: 'raad_ell'
				}"
				:audio-play-event-name="ELL_AUDIO_PLAY"
				:audio-pause-event-name="ELL_AUDIO_PAUSE"
				:audio-complete-event-name="ELL_AUDIO_COMPLETE">
			</audio-player-simple>
		</p>
		<form
			class="form"
			ref="bokForm"
			@submit.prevent="submit">
			<validated-textarea
				class="margin-bottom-medium"
				:value="form.bok"
				@input.native="form.bok = $event.target.value"
				:disabled="isDemo || workspaceSubmitted"
				:min-words="minWords"
				@change.native="saveDraft"
				@paste.native="onPaste"></validated-textarea>
			<button
				class="button -large -wide -centered"
				:disabled="submitDisabled"
				type="submit">
				Submit
			</button>
		</form>
	</div>
</template>
<style lang="scss">
	@import 'config/variables';

	.bok-tab .form {
		.validated-textarea {
			margin-bottom: $size-medium;

			textarea {
				height: 10rem;
			}
		}

		.auto-save {
			float: right;
		}
	}
</style>
